import { normalizeQuotesForHighlightMatch, stripMarkdownForHighlightSnippet } from "./agentHighlightSnippet";
import { stripManuscriptSpacerLines } from "./manuscriptPassageBreakpoints";
import { stripAgentInvisibleMarkup } from "../shifty/shared/markdownSanitization";

const ESCAPED_CLOSE_TAG_RE = /<\\\//g;
/** Broken tag tails left by partial edits, e.g. `inserted>Good` without a leading `<`. */
const MALFORMED_TAG_FRAGMENT_RE =
  /(?:^|[^<])(?:inserted|insertion|insert|deleted|deletion|delete|annotation)\s*>/gi;

/** Visible prose for comparing editor selections to stored markdown spans. */
export function stripManuscriptMarkupForPassageMatch(text: string): string {
  let value = String(text ?? "").replace(ESCAPED_CLOSE_TAG_RE, "</");
  value = stripAgentInvisibleMarkup(value);
  value = value.replace(MALFORMED_TAG_FRAGMENT_RE, "");
  // ProseMirror markdown serializes hard line breaks as backslash-newline; editor plain text omits them.
  value = value.replace(/\\\r?\n/g, "");
  return stripMarkdownForHighlightSnippet(value);
}

export function normalizeManuscriptPassageForMatch(text: string): string {
  return normalizeQuotesForHighlightMatch(
    stripManuscriptSpacerLines(stripManuscriptMarkupForPassageMatch(text)),
  )
    .normalize("NFKC")
    .replace(/[\u00AD\u200B-\u200D\u2060\uFEFF]/g, "")
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * True when two passages refer to the same visible manuscript text.
 * Tolerates agent `<inserted>` / `<annotation>` markup and minor whitespace drift.
 */
export function manuscriptPassagesMatchForReplace(
  selectionText: string,
  passage: string,
  options?: { minOverlapRatio?: number },
): boolean {
  const minRatio = options?.minOverlapRatio ?? 0.98;
  const selection = normalizeManuscriptPassageForMatch(selectionText);
  const target = normalizeManuscriptPassageForMatch(passage);
  if (!selection || !target) return false;
  if (selection === target) return true;
  if (target.includes(selection) || selection.includes(target)) {
    const shorter = Math.min(selection.length, target.length);
    const longer = Math.max(selection.length, target.length);
    if (longer > 0 && shorter / longer >= minRatio) return true;
    // Stale cached passage contained in the live editor selection.
    if (selection.includes(target) && target.length >= 8) return true;
    // Selection fragment contained in the stored passage (e.g. "Aria's" in a longer sentence).
    if (target.includes(selection) && selection.length >= 4) return true;
  }
  return false;
}

/**
 * True when a located markdown span is large enough to replace the full target passage.
 * Rejects under-sized spans that only match a prefix of a multi-paragraph selection.
 */
export function manuscriptLocatedSpanCoversPassage(
  locatedText: string,
  passage: string,
  options?: { minCoverageRatio?: number },
): boolean {
  if (!manuscriptPassagesMatchForReplace(locatedText, passage)) return false;

  const located = normalizeManuscriptPassageForMatch(locatedText);
  const target = normalizeManuscriptPassageForMatch(passage);
  if (!located || !target) return false;
  if (located === target) return true;

  const minCoverage = options?.minCoverageRatio ?? 0.95;
  const shorter = Math.min(located.length, target.length);
  const longer = Math.max(located.length, target.length);
  const ratio = longer > 0 ? shorter / longer : 1;
  if (ratio >= minCoverage) return true;

  // Over-expanded locate: shrink before apply.
  if (located.length > target.length && located.includes(target) && target.length >= 8) {
    return true;
  }

  // Stale wire/cache passage shorter than the live located span.
  if (located.length > target.length && located.includes(target)) {
    return true;
  }

  // Under-sized span: would leave trailing original paragraphs in place.
  if (target.length > located.length && target.includes(located) && located.length >= 8) {
    return false;
  }

  return ratio >= 0.98;
}
